import { createApi } from "@reduxjs/toolkit/query/react";
import { SubjectByIdResponse } from "@src/pages/syllabus/create-syllabus/DefaultTypes";
import { SubejectByCategoryResponse } from "@src/pages/task-manager/tournaments/create-tournament/types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import JSZip from "jszip";
import { APIResponse } from "./CommonApiSlice";
import { Subject } from "./SyllabusApiSlice";
import { OrganizationSyllabusSubject } from "@src/pages/quiz/create-quiz-drawer/task-manager/types&validations";
import { SubjectType } from "@src/pages/subject/subject-base-component/SubjectBaseComponent.types";

interface AddSubjectPayload {
  name: string;
  category: string;
  questionType: string;
  description: string;
  overview?: string;
  isActive: boolean;
  defaultScript?: string;
}
interface EditSubjectPayload {
  name?: string;
  category?: string;
  questionType?: string;
  description?: string;
  overview?: string;
  isActive?: boolean;
  defaultScript?: string;
}

export interface GetAllSubjectsResponse extends APIResponse {
  response: { content: Subject[] };
}

export interface GetSTMSyllabusForDropdownResponse extends APIResponse {
  response: OrganizationSyllabusSubject[];
}

export interface GetSubjectsForDropdown extends APIResponse {
  response: OrganizationSyllabusSubject["subject"][];
}

export interface GetAllSubjectsForOrg extends APIResponse {
  response: SubjectType[];
}

export interface AddResourcePayload {
  parentId: string;
  type: "tutorial" | "playground";
  file: File;
}

export interface GetResourcePayload {
  parentId: string;
  type?: string;
}

export interface BulkUploadSubjectseResponse extends APIResponse {
  response: string;
}

export interface AddSubjectIconPayload {
  subjectId: string;
  file: File;
}

export interface GetObjectiveOrNotesPayload {
  parentId: string;
  parentType: string;
  type: string;
  batchId?: string;
}

export interface GetObjectiveOrNotesResponse extends APIResponse {
  response: string[] | string;
}

export interface UpdateObjectivePayload {
  resourceId: string;
  parentType: string;
  objective: string[];
}

export interface UpdateResourceNotesPayload {
  id: string;
  notes: string;
}

export interface VideoStreamSessionRequest {
  resourceId: string;
}

export interface VideoStreamSessionResponse {
  resourceId: string;
  status: "PREPARING" | "READY" | "FAILED";
  message: string;
  manifestPath: string | null;
  keyPath: string | null;
  statusPath: string;
}

export interface VideoStreamSessionAPIResponse extends APIResponse {
  response: VideoStreamSessionResponse;
}

export const SubjectApiSlice = createApi({
  reducerPath: "subjectApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getAllSubjects: builder.query<
      GetAllSubjectsResponse,
      { pageNo: number; pageSize: number; userEmail?: string; search?: string }
    >({
      query: ({ pageNo, pageSize, search }) => {
        let url = `subject?pageNo=${pageNo}&pageSize=${pageSize}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),
    getSubjectsForDropdown: builder.query<GetSubjectsForDropdown, void>({
      query: () => ({
        url: "subject/get-subjects",
        method: "GET",
      }),
    }),
    getSTMSyllabusForDropdown: builder.query<
      GetSTMSyllabusForDropdownResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `syllabus/get-organization-syllabus?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    getAllSubjectsForOrganization: builder.query<
      GetAllSubjectsForOrg,
      { organizationId: string; pageNo?: number; pageSize?: number; search?: string }
    >({
      query: ({ organizationId, pageNo, pageSize, search }) => {
        let url = `subject/get-subjects-organization?organizationId=${organizationId}`;
        if (pageNo !== undefined) url += `&pageNo=${pageNo}`;
        if (pageSize !== undefined) url += `&pageSize=${pageSize}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getAllSubjectsForOrganizationStudent: builder.query<
      GetAllSubjectsForOrg,
      { organizationId: string; pageNo: number; pageSize: number }
    >({
      query: ({ organizationId, pageNo, pageSize }) => ({
        url: `subject/get-student-subjects?organizationId=${organizationId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    addSubjects: builder.mutation<any, AddSubjectPayload>({
      query: (payload) => ({
        url: "subject/add",
        method: "POST",
        body: payload,
      }),
    }),
    editSubjects: builder.mutation<
      any,
      { subjectId: string } & EditSubjectPayload
    >({
      query: ({ subjectId, ...payload }) => ({
        url: `subject?subjectId=${subjectId}`,
        method: "PUT",
        body: payload,
      }),
    }),
    deleteSubjects: builder.mutation<any, { subjectId: string }>({
      query: ({ subjectId }) => ({
        url: `subject?subjectId=${subjectId}`,
        method: "DELETE",
      }),
    }),
    getSubjectById: builder.query<
      { statusCode: number; response: SubjectByIdResponse },
      { subjectId: string }
    >({
      query: ({ subjectId }) => ({
        url: `subject/get?subjectId=${subjectId}`,
        method: "GET",
      }),
    }),
    getSubject: builder.query<
      any,
      { subjectId: string }
    >({
      query: ({ subjectId }) => ({
        url: `subject/get-subject?subjectId=${subjectId}`,
        method: "GET",
      }),
    }),
    getSubjectByCategory: builder.query<
      SubejectByCategoryResponse,
      { category: string; organizationId?: string }
    >({
      query: ({ category, organizationId }) => {
        let url = `subject/get-category-subjects?category=${category}`;
        if (organizationId) {
          url += `&organizationId=${organizationId}`;
        }
        return {
          url: url,
          method: "GET",
        };
      },
    }),
    addResourceForParent: builder.mutation<any, AddResourcePayload>({
      query: ({ parentId, type, file }) => {
        const formData = new FormData();
        formData.append("parentId", parentId);
        formData.append("type", type);
        formData.append("file", file);

        return {
          url: "text-files/upload",
          method: "POST",
          body: formData,
          responseHandler: async (response) => {
            const text = await response.text();
            return { response: text };
          },
        };
      },
    }),

    getResourceForParent: builder.query<Blob, GetResourcePayload>({
      query: ({ parentId, type = "tutorial" }) => {
        let url = `text-files/download?parentId=${parentId}&type=${type}`;
        return {
          url,
          method: "GET",
          responseHandler: async (response) => {
            const blob = await response.blob();
            return blob;
          },
        };
      },
    }),
    bulkUploadSubjects: builder.mutation<
      BulkUploadSubjectseResponse,
      { file: FormData }
    >({
      query: ({ file }) => {
        const URL = "question/bulkUploadSubjects";
        return {
          url: URL,
          method: "POST",
          body: file,
        };
      },
    }),
    getBulkUploadSubjectsTemplate: builder.query<Blob, void>({
      query: () => ({
        url: "question/bulkUploadSubjects/template",
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
    }),
    getObjectiveOrNotes: builder.query<
      GetObjectiveOrNotesResponse,
      GetObjectiveOrNotesPayload
    >({
      query: ({ parentId, parentType, type, batchId }) => {
        let url = `student/get-objective-or-notes?parentId=${parentId}&parentType=${parentType}&type=${type}`;
        if (batchId) {
          url += `&batchId=${batchId}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),
    updateObjective: builder.mutation<any, UpdateObjectivePayload>({
      query: ({ resourceId, parentType, objective }) => {
        const objArray = Array.isArray(objective) ? objective : [objective];
        return {
          url: "subject/update-objective",
          method: "PATCH",
          body: {
            resourceId,
            parentType,
            objective: objArray,
          },
        };
      },
    }),
    updateResourceNotes: builder.mutation<any, UpdateResourceNotesPayload>({
      query: ({ id, notes }) => ({
        url: `student/update-resource-notes?id=${id}`,
        method: "PATCH",
        body: { notes },
      }),
    }),
    downloadTextFile: builder.query<
      { content: string; extension: string },
      { parentId: string; type: string }
    >({
      query: ({ parentId, type }) => ({
        url: `text-files/download?parentId=${parentId}&type=${type}`,
        method: "GET",
        responseHandler: async (response) => {
          const contentType = response.headers.get("content-type") || "";
          let content = "";
          let extension = "java";

          const contentDisposition = response.headers.get(
            "content-disposition",
          );
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";]+)"?/i);
            if (match && match[1]) {
              const filename = match[1];
              extension = filename.split(".").pop() || "java";
            }
          }

          try {
            const arrayBuffer = await response.clone().arrayBuffer();
            const zip = new JSZip();
            const zipContents = await zip.loadAsync(arrayBuffer);

            const files = Object.keys(zipContents.files).filter(
              (f) => !zipContents.files[f].dir,
            );

            console.log("[Playground ZIP] Files inside:", files);

            const codeExtensions = [
              ".java",
              ".py",
              ".cpp",
              ".c",
              ".js",
              ".ts",
              ".txt",
            ];
            let targetFile = files.find((f) =>
              codeExtensions.some((ext) => f.toLowerCase().endsWith(ext)),
            );

            if (!targetFile && files.length > 0) {
              targetFile = files[0];
            }

            if (targetFile) {
              content = await zipContents.files[targetFile].async("text");
              extension = targetFile.split(".").pop() || "java";
            }
          } catch (zipErr) {
            console.warn(
              "[Playground ZIP] Not a valid ZIP, falling back to raw text:",
              zipErr,
            );
            content = await response.text();
          }

          return { content, extension };
        },
      }),
    }),
    downloadTextFileElectron: builder.query<
      { blob: Blob; extension: string },
      { parentId: string; type: string }
    >({
      query: ({ parentId, type }) => {
        const baseUrl = import.meta.env.VITE_CMS_SUBJECT_SERVICE || "";
        let url = `${baseUrl}text-files/download?parentId=${parentId}&type=${type}`;
        return {
          url,
          method: "GET",
          responseHandler: async (response) => {
            const blob = await response.blob();
            const contentDisposition = response.headers.get(
              "content-disposition",
            );
            let extension = "zip";
            if (contentDisposition) {
              const match = contentDisposition.match(/filename="?([^";]+)"?/i);
              if (match && match[1]) {
                const filename = match[1];
                extension = filename.split(".").pop() || "zip";
              }
            } else {
              const contentType = response.headers.get("content-type") || "";
              if (contentType.includes("pdf")) {
                extension = "pdf";
              } else if (
                contentType.includes("markdown") ||
                contentType.includes("text")
              ) {
                extension = "md";
              } else if (contentType.includes("zip")) {
                extension = "zip";
              }
            }
            return { blob, extension };
          },
        };
      },
    }),
    startVideoStreamSession: builder.mutation<VideoStreamSessionAPIResponse, VideoStreamSessionRequest>({
      query: (payload) => ({
        url: "subject/video/stream/session",
        method: "POST",
        body: payload,
      }),
    }),
    getVideoStreamStatus: builder.query<VideoStreamSessionAPIResponse, { resourceId: string }>({
      query: ({ resourceId }) => ({
        url: `subject/video/stream/${resourceId}/status`,
        method: "GET",
      }),
    }),
    addSubjectIcon: builder.mutation<APIResponse, AddSubjectIconPayload>({
      query: ({ subjectId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `subject/add-icon?subjectId=${subjectId}`,
          method: "PATCH",
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGetAllSubjectsQuery,
  useAddSubjectsMutation,
  useEditSubjectsMutation,
  useDeleteSubjectsMutation,
  useGetAllSubjectsForOrganizationQuery,
  useLazyGetAllSubjectsForOrganizationQuery,
  useGetSubjectByIdQuery,
  useLazyGetSubjectByIdQuery,
  useGetSubjectQuery,
  useLazyGetSubjectQuery,
  useLazyGetSubjectByCategoryQuery,
  useLazyGetAllSubjectsQuery,
  useLazyGetSubjectsForDropdownQuery,
  useGetSubjectsForDropdownQuery,
  useLazyGetSTMSyllabusForDropdownQuery,
  useAddResourceForParentMutation,
  useLazyGetResourceForParentQuery,
  useGetResourceForParentQuery,
  useBulkUploadSubjectsMutation,
  useLazyGetBulkUploadSubjectsTemplateQuery,
  useGetAllSubjectsForOrganizationStudentQuery,
  useLazyGetAllSubjectsForOrganizationStudentQuery,
  useGetObjectiveOrNotesQuery,
  useLazyGetObjectiveOrNotesQuery,
  useUpdateObjectiveMutation,
  useUpdateResourceNotesMutation,
  useDownloadTextFileQuery,
  useLazyDownloadTextFileQuery,
  useDownloadTextFileElectronQuery,
  useLazyDownloadTextFileElectronQuery,
  useStartVideoStreamSessionMutation,
  useGetVideoStreamStatusQuery,
  useLazyGetVideoStreamStatusQuery,
  useAddSubjectIconMutation,
} = SubjectApiSlice;
